import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, ArrowUpRight, Share2 } from 'lucide-react';
import { blogPosts } from '../mock';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useToast } from '../hooks/use-toast';


const BlogPostPage = ({ onOpenBooking, onOpenLogin }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
        <Header onLogin={onOpenLogin} onCtaBook={onOpenBooking} />
        <div className="flex-1 flex items-center justify-center px-6 text-center">
          <div>
            <p className="text-amber-400 tracking-[0.32em] text-[12px] font-medium mb-4">404</p>
            <h1 className="font-serif text-4xl mb-4">Article not found</h1>
            <p className="text-white/60 mb-8">The post you are looking for may have moved or been removed.</p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 text-[#0a0a0a] font-medium hover:bg-amber-300 transition-colors"
            >
              <ArrowLeft size={16} /> Back to blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const related = blogPosts.filter((p) => p.id !== post.id && p.category==post.category).slice(0, 2);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: post.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast({ title: 'Link copied', description: 'Article URL copied to clipboard.' });
      }
    } catch (err) {
      // Common when the user cancels the share sheet or clipboard is blocked
      // by the browser. Not a real failure — silently swallow.
      void err;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Header onLogin={onOpenLogin} onCtaBook={onOpenBooking} />

      {/* Hero */}
      <section className="relative pt-32 pb-16">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={post.image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/70 via-[#0a0a0a]/90 to-[#0a0a0a]" />
        </div>
        <div className="relative max-w-[860px] mx-auto px-6 lg:px-10">
          <button
            onClick={() => navigate('/blog')}
            className="inline-flex items-center gap-2 text-white/60 hover:text-amber-400 text-sm transition-colors mb-8"
          >
            <ArrowLeft size={16} /> Back to blog
          </button>
          <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-[58px] leading-[1.08] tracking-tight">
            {post.title}
          </h1>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-white/55 text-sm">
            <span className="flex items-center gap-2">
              <User size={14} className="text-amber-400" /> {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar size={14} className="text-amber-400" /> {post.date}
            </span>
            <span className="flex items-center gap-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-400 text-xs font-medium tracking-wider ">
                {post.category}
              </div>
            </span>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 hover:text-amber-400 transition-colors ml-auto"
            >
              <Share2 size={14} /> Share
            </button>
          </div>
        </div>
      </section>

      {/* Featured image */}
      <section className="relative -mt-4">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
          <div className="rounded-2xl overflow-hidden border border-white/10 aspect-[16/9]">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="relative py-16">
        <div className="max-w-[760px] mx-auto px-6 lg:px-10">
          <article className="space-y-6">
            {post.content.map((block, i) => {
              // Composite key: blog content is static so index is functionally safe,
              // but we mix in the block type to make keys more discriminative if
              // a block is ever inserted between renders.
              const blockKey = `block-${i}-${block.type}`;
              if (block.type === 'h2') {
                return (
                  <h2
                    key={blockKey}
                    className="font-serif text-white text-3xl sm:text-[34px] leading-tight mt-12 mb-2"
                  >
                    {block.text}
                  </h2>
                );
              }
              if (block.type === 'quote') {
                return (
                  <blockquote
                    key={blockKey}
                    className="my-10 pl-6 border-l-2 border-amber-400 text-white/85 font-serif italic text-2xl leading-relaxed"
                  >
                    “{block.text}”
                  </blockquote>
                );
              }
              if (block.type === 'list') {
                return (
                  <ul key={blockKey} className="space-y-3 ">
                    {block.items.map((item) => (
                      <li
                        key={item.title}
                        className="text-white/75 my-7 leading-relaxed"
                      >
                        <span className="flex text-amber-400 text-[24px] gap-3 mt-2 shrink-0">•<span> {item.title}</span></span>
                        <p className="text-[17px] mt-2 shrink-0 my-10 pl-6 border-l-2 border-white/50 text-2xl leading-relaxed">{item.text}</p>
                      </li>
                    ))}
                  </ul>
                );
              }
              return (
                <p
                  key={blockKey}
                  className="text-white/75 text-[17.5px] leading-[1.85]"
                >
                  {block.text}
                </p>
              );
            })}
          </article>

          {/* CTA */}
          <div className="mt-16 p-8 rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-400/[0.07] to-transparent">
            <p className="text-amber-400 text-[11px] tracking-[0.32em] font-medium">READY TO IMPROVE</p>
            <h3 className="font-serif text-white text-2xl sm:text-3xl mt-2">
              Want personal feedback on your games?
            </h3>
            <p className="mt-3 text-white/65 leading-relaxed">
              Book a 1-on-1 lesson and I will help you apply these ideas directly to your own play.
              Clear progress you can feel
            </p>
            <button
              onClick={onOpenBooking}
              className="mt-6 inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-amber-400 text-[#0a0a0a] hover:bg-amber-300 transition-colors font-medium"
            >
              Book Your Lesson Now <ArrowUpRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="relative py-20 border-t border-white/5">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
            <p className="text-amber-400 tracking-[0.32em] text-[12px] font-medium mb-3">KEEP READING</p>
            <h2 className="font-serif text-white text-3xl sm:text-4xl mb-10">Related articles</h2>
            <div className="grid md:grid-cols-2 gap-7">
              {related.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="group rounded-2xl overflow-hidden bg-white/[0.02] border border-white/10 hover:border-amber-400/40 transition-all duration-300 flex flex-col sm:flex-row"
                >
                  <div className="sm:w-48 h-44 sm:h-auto shrink-0 overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-6 flex-1">
                    <div className="text-amber-400 text-xs tracking-wider">{p.category}</div>
                    <h3 className="mt-2 font-serif text-white text-xl leading-snug group-hover:text-amber-400 transition-colors">
                      {p.title}
                    </h3>
                    <div className="mt-3 text-white/50 text-xs flex items-center gap-2">
                      <Clock size={12} /> {p.readTime}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPostPage;
